import {
  NO_BLOCKER_QUALIFIER,
  type AuthorityLevel, type ConnectionScenario, type RegulatoryIssueId, type RegulatoryIssueStatus, type SourceRuleStatus,
} from '../regulatoryFeasibility/types';

export const SUMMARY_SCHEMA_VERSION = 'screening-summary-v1' as const;
/**
 * 1.4.0 (regulatory screening, R6+R7): no hard-coded islanded pathway, a
 * qualified Regulatory tier, explicit open air/land-use items and the frozen
 * per-issue regulatory status table. Historical 1.2.0, 1.3.0 and 1.3.1
 * artifacts stay parseable and downloadable (reportArtifactJobContracts.ts).
 */
// 1.5.0: conservative frozen decision conflict presentation; old artifacts stay addressable.
// 1.6.0: frozen recommendation intent leads with saved advice and preserves proposal inputs separately.
// 1.6.1: per-issue regulatory confirmation/decision gates and explicit cooling/fuel prerequisites.
// 1.6.2: neutral historical form-guidance notes, explicit waterScreeningView v2 model vs ASHRAE psychrometric methodology separation, and fresh summary artifact identity.
export const SUMMARY_BUILDER_VERSION = '1.6.2' as const;
/**
 * Full-report PDF artifact builder version. Historical 1.0.0 jobs (where
 * pdfBuilderVersion was omitted) keep their original requestHash and jobId;
 * 1.1.0 computes a distinct deterministic job identity so new full-report PDF
 * exports never mutate or invalidate historical succeeded jobs or existing PDFs.
 */
export const REPORT_PDF_BUILDER_VERSION = '1.1.0' as const;
/** EPA FRS counts are a frozen dataset the full report does not render yet (owner decision D2). */
export const INCLUDE_FRS_ROW = false;

// ─── Static vocabulary (the ONLY editorial copy the builder owns) ──────────────

/** Short scorecard labels, keyed by the scorer's category id. */
export const PILLAR_SHORT_LABEL: Record<string, string> = {
  grid: 'Grid & power',
  cooling: 'Cooling & water',
  site: 'Site & land',
  regulatory: 'Regulatory',
  logistics: 'Logistics',
  risk_economics: 'Risk & economics',
};

/** Short metric labels used in the scorecard qualifier sentence. */
export const METRIC_SHORT_LABEL: Record<string, string> = {
  nuclear_friend: 'nuclear permitting',
  reactor_proximity: 'reactor proximity',
  zoning: 'zoning',
  env_const: 'environment',
  flood_risk: 'Flood',
  seismic_risk: 'Seismic',
  air_quality: 'Air quality',
  topography: 'Topography',
  land_avail: 'Land availability',
  amb_temp: 'Ambient temperature',
  wet_bulb: 'Wet bulb',
  water_stress: 'Water stress',
  firm_capacity: 'Firm capacity',
  coverage: 'modeled ELCC coverage',
  sink: 'heat-rejection sink',
  water_source_stress: 'water source & stress',
  waste_heat_pue: 'waste-heat & PUE',
  wet_bulb_secondary: 'wet-bulb (secondary)',
  road_prox: 'Road access',
};

/**
 * Shown with the scorecard whenever a Regulatory category is shown. The tier
 * is the saved screening score; it never reads as a clearance. Kept short so
 * it sits on the scorecard heading line and adds no height to page 1.
 */
export const REGULATORY_SCORECARD_QUALIFIER = 'Regulatory tier: not a permit, approval or legal clearance' as const;

/** Also-open wording when on-site combustion is recorded but the saved report
 * carries no air-permit risk. Never says "not required", "exempt" or "cleared". */
export const OPEN_AIR_PERMIT_ITEM = 'air-permit applicability and permitted operating envelope (emergency vs prime duty)' as const;
/** Also-open wording when no zoning document was on file at generation. */
export const OPEN_LAND_USE_ITEM = 'land-use approval for both data-center and generation uses' as const;

/** Stage contract line, keyed by screeningBasis.stage. */
export const STAGE_CONTRACT: Record<number, string> = {
  1: 'Stage 1 location screen: not an engineering certification or a final investment decision.',
};

export type ThemeId = 'footprint' | 'power' | 'cooling' | 'access' | 'regulatory';

/** Condition themes: imperative title (the "conditions to proceed" voice).
 * A risk theme listed nowhere is not grouped (never filed under access). */
export const CONDITION_THEMES: Record<ThemeId, { title: string; members: readonly string[] }> = {
  footprint: { title: 'Confirm the buildable footprint', members: ['flood', 'land', 'wetlands', 'geotech', 'environmental'] },
  power: { title: 'Firm up power and fuel', members: ['grid', 'air', 'fuel', 'interconnection'] },
  cooling: { title: 'Set the cooling and water basis', members: ['water', 'cooling', 'heat'] },
  access: { title: 'Confirm access and logistics', members: ['logistics', 'connectivity'] },
  regulatory: { title: 'Confirm the regulatory approval path', members: ['regulatory', 'land-use', 'zoning', 'electricity-sale', 'grid-interface', 'grid-service'] },
};

// ─── Regulatory feasibility table (R7; frozen screen, display only) ───────────

/** The owner's four statuses, sentence case for display. */
export const REGULATORY_STATUS_LABEL: Readonly<Record<RegulatoryIssueStatus, string>> = {
  'no-blocker-identified': 'No blocker identified',
  'approval-required': 'Approval required',
  unresolved: 'Unresolved',
  'confirmed-restriction': 'Confirmed restriction',
};
/** Shown instead of a pass or a restriction whose source the report does not admit. */
export const REGULATORY_SOURCE_NOT_ADMITTED_LABEL = 'Unresolved (source not admitted)' as const;
/** Re-exported so the Summary prints the screen's own qualifier text verbatim. */
export { NO_BLOCKER_QUALIFIER };

export const REGULATORY_ISSUE_SHORT_LABEL: Readonly<Record<RegulatoryIssueId, string>> = {
  'land-use-data-center': 'Land use (data center)',
  'land-use-generation': 'Land use (on-site generation)',
  'air-permit-operating-duty': 'Air permit (operating duty)',
  'ownership-electricity-sale': 'Ownership (electricity sale)',
  'grid-interface': 'Grid interface',
  'grid-service-economics': 'Grid service (economics)',
};

export const REGULATORY_SCENARIO_LABEL: Readonly<Record<ConnectionScenario, string>> = {
  islanded: 'Islanded',
  'grid-parallel-non-export': 'Grid-parallel, non-export',
  'grid-parallel-export': 'Grid-parallel, exporting',
  unknown: 'Connection mode not recorded',
};

/** Scenario prefix for the per-scenario grid-interface statuses in the Summary. */
export const REGULATORY_SCENARIO_SHORT_LABEL: Readonly<Record<ConnectionScenario, string>> = {
  islanded: 'Islanded',
  'grid-parallel-non-export': 'Non-export',
  'grid-parallel-export': 'Exporting',
  unknown: 'Mode not recorded',
};

/** Short display form of the not-an-approval qualifier, printed beside every
 * 'No blocker identified' status. */
export const NO_BLOCKER_DISPLAY_QUALIFIER = 'not an approval or legal clearance' as const;

/** A proposed rule, a proceeding or an unknown status never reads as effective. */
export const REGULATORY_RULE_STATUS_LABEL: Readonly<Record<SourceRuleStatus, string>> = {
  effective: 'effective rule',
  proposed: 'proposed rule, not effective',
  proceeding: 'open proceeding, not effective',
  unidentified: 'rule not identified',
  unknown: 'rule status not established',
};

export const REGULATORY_AUTHORITY_LEVEL_LABEL: Readonly<Record<AuthorityLevel, string>> = {
  local: 'Local', state: 'State', federal: 'Federal', utility: 'Utility', 'rto-iso': 'RTO/ISO', unknown: 'Authority not identified',
};

/** Caption of the Summary's regulatory table. */
export const REGULATORY_TABLE_QUALIFIER = 'Screening statuses, not permits, approvals or legal clearances; each issue’s authority, dated sources and next action are in the full report appendix.' as const;

/** Also-open disclosure for a revision that carries no frozen screen. */
export const REGULATORY_NOT_SCREENED_OPEN = 'regulatory feasibility not screened in this revision' as const;

/** What each risk theme's next step resolves (static, per theme). */
export const RESOLVES: Record<string, string> = {
  flood: 'Buildable acreage; whether fill or elevation is needed',
  air: 'Air-permit pathway for on-site gas',
  grid: 'Grid capacity, upgrades, cost and timing',
  fuel: 'Single-path fuel dependency',
  cooling: 'Cooling strategy and heat rejection',
  water: 'Water supply and rights',
  wetlands: 'Section 404 applicability',
  environmental: 'Parcel environmental status and permit scope',
  geotech: 'Foundation design',
  regulatory: 'Approval path and authority',
};

/** Water-screen row short labels, keyed by the catalog scenario id. */
export const WATER_ROW_LABEL: Record<string, string> = {
  'modern-operator-fleet-wue-v1': 'Total site · operator-fleet benchmark',
  'lbnl-national-current-average-wue-v2': 'Total site · LBNL national average',
  'epa-wet-cooling-tower-water-v2-consumption': 'Wet cooling tower · evaporation',
  'epa-wet-cooling-tower-water-v2-withdrawal': 'Wet cooling tower · makeup withdrawal',
  'dry-cooling-process-water-v1': 'Dry heat rejection · adiabatic assist',
};

/** BTM technology short labels (btmOptions[].technology). */
export const BTM_SHORT_LABEL: Record<string, string> = {
  'Natural Gas (Combined Cycle/Simple Cycle)': 'Natural gas (CC/SC)',
  'Solar + BESS': 'Solar + BESS',
  'Nuclear SMR': 'Nuclear SMR',
};

/** Short source names for the sources line, by reference-source prefix (fallback: the cut rule). */
export const SOURCE_SHORT: ReadonlyArray<[string, string]> = [
  ['FEMA National Flood Hazard Layer', 'FEMA NFHL'],
  ['HIFLD Electric Substations', 'HIFLD substations'],
  ['PeeringDB', 'PeeringDB'],
  ['Deterministic elevation-relief', 'Elevation proxy'],
  ['Open-Meteo Historical Weather API', 'Open-Meteo (2021–2025)'],
  ['AirNow EPA Real-Time AQI', 'AirNow'],
  ['USDA NRCS Web Soil Survey', 'USDA SSURGO'],
  ['EIA Electricity Retail Sales', 'EIA retail sales'],
  ['WRI Aqueduct Global Maps 2.1', 'WRI Aqueduct 2.1'],
  ['ASHRAE, 2021 ASHRAE Handbook', 'ASHRAE 2021 Psychrometrics (methodology)'],
  ['EVAPCO,', 'EVAPCO adiabatic cooler guide'],
  ['SPX Cooling Technologies, Marley', 'SPX Marley OlympusV data'],
  ['SPX Cooling Technologies, Managing', 'SPX adiabatic water guide'],
];

/** Intent fields recorded as unknown → the "also open" wording. */
export const OPEN_FIELD_LABEL: Record<string, string> = {
  pueValue: 'PUE',
  workloadProfile: 'workload profile',
  interconnectionStatus: 'interconnection status',
  inServiceDate: 'in-service dates',
  waterSource: 'water source',
  nuclearInterest: 'nuclear interest',
};
